import { Link } from "@inertiajs/react";
import {
  CirclePlusIcon,
  EllipsisIcon,
  SquareCheckIcon,
  SquareXIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Counterparty, PropsWithUser } from "@/types";
import { PageTitle } from "@/components/page-title";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TableCellLink } from "@/components/table-cell-link";

type Props = {
  counterparties: Counterparty[];
};

function CounterpartyIndex({ counterparties }: PropsWithUser<Props>) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Counterparties</PageTitle>
        <Button asChild>
          <Link href="/counterparties/new">
            <CirclePlusIcon />
            Add counterparty
          </Link>
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-0">Auto reconcile</TableHead>
              <TableHead className="w-0">VAT rate</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {counterparties.map((counterparty) => {
              const href = `/counterparties/${counterparty.id}`;

              return (
                <TableRow
                  key={counterparty.id}
                  className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
                >
                  <TableCell className="font-medium">
                    <TableCellLink href={href} />
                    {counterparty.name}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    {counterparty.auto_reconcile ? (
                      <SquareCheckIcon className="text-accent-foreground size-5" />
                    ) : (
                      <SquareXIcon className="text-muted-foreground size-5" />
                    )}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} />
                    {counterparty.vat_rate}%
                  </TableCell>
                  <TableCell>
                    <CounterpartyActions counterparty={counterparty} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function CounterpartyActions({ counterparty }: { counterparty: Counterparty }) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete counterparty</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete{" "}
            <span className="font-semibold">{counterparty.name}</span>{" "}
            counterparty.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/counterparties/${counterparty.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default CounterpartyIndex;
